import React, { useState } from 'react';
import { Plus, Shield, RefreshCw } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { Button } from '../../components/ui/button';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { AclRuleModal } from './components/AclRuleModal';
import { useAclRulesList } from './hooks/useAclRulesList';
import { aclApi } from './api/aclApi';
import type { AclNode } from './types';
import { useAclConfigStore } from '../../store/aclConfig';

export default function AclPage() {
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AclNode | null>(null);
  
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    rule: AclNode | null;
  }>({ isOpen: false, rule: null });

  const { markPendingChanges } = useAclConfigStore();

  const {
    rules,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useAclRulesList();

  const handleAdd = () => {
    setEditingRule(null);
    setIsModalOpen(true);
  };

  const handleEdit = (rule: AclNode) => {
    setEditingRule(rule);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (rule: AclNode) => {
    setDeleteDialog({ isOpen: true, rule });
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialog.rule) return;
    
    try {
      await aclApi.delete(deleteDialog.rule.id);
      showSuccess(t('acl.notifications.deleteSuccess', 'ACL Rule deleted successfully'));
      markPendingChanges();
      refresh();
    } catch (error: any) {
      showError(error.message || 'Failed to delete ACL Rule');
    } finally {
      setDeleteDialog({ isOpen: false, rule: null });
    }
  };


  // Add the actions column render
  const columnsWithActions = columns.map(col => {
    if (col.key === 'actions') {
      return {
        ...col,
        render: (_value: unknown, item: AclNode) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleEdit(item)}
              className="h-8 px-3"
            >
              {t('common.edit', 'Edit')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteClick(item)}
              className="h-8 px-3"
            >
              {t('common.delete', 'Delete')}
            </Button>
          </div>
        )
      };
    }
    return col;
  });

  return (
    <div className="p-6">
      <DataGridSimple
        title={t('acl.title', 'Access Control Lists (ACL)')}
        titleIcon={<Shield className="h-5 w-5 text-primary" />}
        searchPlaceholder={t('common.search', 'Search...')}
        data={rules}
        columns={columnsWithActions}
        loading={loading}
        pagination={pagination}
        searchQuery={searchQuery}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onColumnToggle={handleColumnToggle}
        actions={
          <div className="flex items-center gap-2">
            <SimpleTooltip content={t('common.add', 'Add Rule')} position="top">
              <button
                onClick={handleAdd}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" /> {t('common.add', 'Add Rule')}
              </button>
            </SimpleTooltip>
          </div>
        }
      />

      <AclRuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        rule={editingRule}
        onSuccess={() => {
          setIsModalOpen(false);
          refresh();
        }}
      />

      <ConfirmationModal
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, rule: null })}
        onConfirm={handleConfirmDelete}
        title={t('acl.modal.deleteTitle', 'Delete ACL Rule')}
        message={t(
          'acl.modal.deleteMessage',
          'Are you sure you want to delete this ACL rule? This action cannot be undone and will immediately apply to FreeSWITCH.'
        )}
        confirmText={t('common.delete', 'Delete')}
        cancelText={t('common.cancel', 'Cancel')}
        type="danger"
      />
    </div>
  );
}
